import { useState } from 'react';
import { Building2Icon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  SkeletonTable,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  type PillTone,
} from '@bcecloud/portal-ui';
import { getList, errorMessage } from '@/lib/api';
import { ORGANIZATION_KINDS, type OrganizationKind, type OrganizationSummary, type OrganizationStatus } from '@/lib/types';
import { PLACEMENT_FILTER, PLACEMENT_FILTERS, PartnerPlacementsTable, type PlacementFilter } from './org-partner-placements';

const STATUS_TONES = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  ARCHIVED: 'neutral',
} as const satisfies Record<OrganizationStatus, PillTone>;

/**
 * The list's kind tab, kept in the URL (`?kind=partner`). Clients and partners
 * are never listed together; Clients is the default (no param).
 */
const KIND_FILTERS = [
  { key: 'client', label: 'Clients' },
  { key: 'partner', label: 'Partners' },
] as const;
type KindFilter = (typeof KIND_FILTERS)[number]['key'];
const DEFAULT_KIND_FILTER: KindFilter = 'client';
const KIND_PARAM: Readonly<Record<KindFilter, OrganizationKind>> = {
  client: ORGANIZATION_KINDS.client,
  partner: ORGANIZATION_KINDS.partner,
};

/**
 * Lists client organizations, or showcase partners on the Partners tab.
 *
 * The portal URL is shown per row because it is the thing staff most often
 * need — to hand to a client, or to open and check a brand change landed.
 *
 * @returns The organizations page.
 */
export function OrganizationsPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [params, setParams] = useSearchParams();
  const kind: KindFilter = KIND_FILTERS.find((k) => k.key === params.get('kind'))?.key ?? DEFAULT_KIND_FILTER;
  const partners = kind === 'partner';
  // Whether partners have put us on their site; only means something on the Partners view.
  const placementParam = params.get('placement');
  const placement: PlacementFilter = (partners ? PLACEMENT_FILTERS.find((p) => p.key === placementParam)?.key : undefined) ?? PLACEMENT_FILTER.all;
  const byPlacement = placement !== PLACEMENT_FILTER.all;

  const organizations = useQuery({
    queryKey: ['organizations', search, kind],
    enabled: !byPlacement,
    queryFn: ({ signal }) =>
      getList<OrganizationSummary>(
        '/organizations',
        { ...(search ? { search } : {}), kind: KIND_PARAM[kind] },
        signal,
      ),
  });
  // A placement tab shows its own table; a cached organization list stays hidden behind it.
  const { data, isPending, error } = byPlacement ? { data: undefined, isPending: false, error: null } : organizations;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="CLIENT PORTALS"
        title="Organizations"
        subtitle="Each client gets a branded portal at their own subdomain. Showcase partners get one too, for their program showcase."
        action={
          <Button guide="organizations-new" variant="primary" to={partners ? '/organizations/new?kind=partner' : '/organizations/new'}>
            {partners ? 'New partner' : 'New organization'}
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SegmentedControl
          label="Show"
          options={KIND_FILTERS}
          value={kind}
          onChange={(next) =>
            setParams((prev) => {
              const updated = new URLSearchParams(prev);
              if (next === DEFAULT_KIND_FILTER) updated.delete('kind');
              else updated.set('kind', next);
              if (next !== 'partner') updated.delete('placement');
              return updated;
            })
          }
        />
        {partners ? (
          <SegmentedControl
            label="Their site"
            guide="organizations-placement"
            options={PLACEMENT_FILTERS}
            value={placement}
            onChange={(next) =>
              setParams((prev) => {
                const updated = new URLSearchParams(prev);
                if (next === PLACEMENT_FILTER.all) updated.delete('placement');
                else updated.set('placement', next);
                return updated;
              })
            }
          />
        ) : null}
        <SearchInput
          label="Search organizations"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or slug"
          className="w-full max-w-sm"
        />
      </div>

      {placement !== PLACEMENT_FILTER.all ? <PartnerPlacementsTable filter={placement} search={search} /> : null}

      {error ? <ErrorNotice title="Organizations unavailable" message={errorMessage(error)} /> : null}

      {isPending ? (
        <SkeletonTable columns={['w-[32%]', 'w-[30%]', 'w-[10%]', 'w-[12%]']} label="Loading organizations…" />
      ) : null}

      {data && data.items.length === 0 ? (
        <Panel>
          {search ? (
            <EmptyState message={`No ${partners ? 'partners' : 'client portals'} match “${search}”.`} />
          ) : (
            <EmptyState
              icon={<Building2Icon aria-hidden className="size-6" />}
              title={partners ? 'No partners yet' : 'No client portals yet'}
              message={
                partners
                  ? 'Add a venue, hotel or nonprofit from the partner program. They choose programs for their showcase and embed it on their site.'
                  : 'Spin up a branded portal for your first client. They get their own subdomain, colors and credit pool.'
              }
              action={
                <Button variant="primary" to={partners ? '/organizations/new?kind=partner' : '/organizations/new'}>
                  {partners ? 'New partner' : 'New organization'}
                </Button>
              }
            />
          )}
        </Panel>
      ) : null}

      {data && data.items.length > 0 ? (
        <DataTable guide="organizations-table" headers={['Name', 'Portal address', 'Members', 'Status']}>
          {data.items.map((organization) => (
            <tr key={organization.id} className="border-t border-portal-line align-middle">
              <td className="p-3">
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="size-8 shrink-0 rounded-md ring-1 ring-black/5"
                    style={{ background: organization.brand?.primaryColor ?? '#00338d' }}
                  />
                  <Link
                    to={`/organizations/${organization.id}`}
                    className="text-portal-small font-bold underline-offset-2 hover:underline"
                  >
                    {organization.name}
                  </Link>
                  {organization.isDemo ? <Pill tone="info">Demo</Pill> : null}
                </div>
              </td>
              <td className="p-3">
                <a
                  href={organization.portalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-portal-small text-brand underline-offset-2 hover:underline"
                >
                  {organization.portalUrl.replace(/^https?:\/\//, '')}
                </a>
              </td>
              <td className="p-3 text-portal-small tabular-nums">{organization.memberCount}</td>
              <td className="p-3">
                <Pill tone={STATUS_TONES[organization.status]}>
                  {organization.status.toLowerCase()}
                </Pill>
              </td>
            </tr>
          ))}
        </DataTable>
      ) : null}
    </section>
  );
}
